import React, { useState, useCallback, useMemo, useContext, useEffect } from 'react';
import {
  Grid,
  Card,
  CardContent,
  CardHeader,
  Stack,
} from '@mui/material';
import { MyButton } from '../../../common/components';
import { IconUsers, IconPlus } from '@tabler/icons';
import useParsedTableData from '../../../common/hooks/useParsedTableData';
import ClinicContext from '../../context/ClinicContext';
import AddClinicProviderDialog from './AddClinicProviderDialog';
import ManageProvidersTable from '../../../auth/Management/ManageProvidersTable';
import clientGraphql from '../../../../common/apollo-graphql';
import { useMutation } from '@apollo/client';
import { DELETE_PROVIDER_MUTATION } from '../../gql/Mutation';
import Swal from 'sweetalert2';
import UpdateClinicProviderDialog from './UpdateClinicProviderDialog';
import { DEFAULT_PROVIDER_NAME } from '../EnrollClinic/ProviderStep';

export default function ClinicProvidersTable({ data, loading }) {
  const { id: clinicId, refetch } = useContext(ClinicContext);
  const [providerData, setProviderData] = useState({});
  const [isProviderDialogVisible, setIsProviderDialogVisible] = useState(false);
  const [isDefaultProviderDialogVisible, setIsDefaultProviderDialogVisible] = useState(false);
  const [updateProviderDialogVisible, setUpdateProviderDialogVisible] = useState(false);

  const [
    delete_clinic_provider,
    { loading: deleteClinicProviderLoading },
  ] = useMutation(DELETE_PROVIDER_MUTATION, {
    client: clientGraphql,
  });

  const handleCloseAddProviderDialog = useCallback((isSuccess = false) => {
    setIsProviderDialogVisible(false);
    setIsDefaultProviderDialogVisible(false);
    if (isSuccess) {
      refetch();
    }
  }, [refetch]);

  const handleUpdateProviderDialogClose = useCallback((isSuccess = false) => {
    setUpdateProviderDialogVisible(false);
    if (isSuccess) {
      refetch();
    }
  }, [refetch]);

  const handleOnAdd = useCallback(() => {
  }, []);

  const handleOnEdit = useCallback((rowId, params) => {
    setUpdateProviderDialogVisible(true);
    setProviderData(params.row);
  }, [setUpdateProviderDialogVisible, setProviderData]);

  const handleDeleteProvider = useCallback((uuid) => {
    Swal.fire({
      title: "Are you sure?",
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Yes',
    }).then((result) => {
      if (result.isConfirmed) {
        delete_clinic_provider({
          client: clientGraphql,
          variables: { uuid: uuid },
        }).then(() => {
          refetch();
        })
      }
    });
  }, [delete_clinic_provider, refetch]);

  const actions = useMemo(() => {
    return { onDelete: handleDeleteProvider, onEditProvider: handleOnEdit };
  }, [handleDeleteProvider, handleOnEdit]);

  const { parsedData, setParsedData } = useParsedTableData(data, actions);
  const [isDefaultPresent, setIsDefaultPresent] = useState(false);

  useEffect(() => {
    if (parsedData && parsedData.rows.length) {
      const isAlreadySorted = parsedData.rows.every((row, idx, arr) => {
        return idx === 0 || !row.is_default || arr[idx - 1].is_default;
      });

      if (!isAlreadySorted) {
        const sortedRows = [...parsedData.rows].sort((a, b) => {
          return (b.is_default === true) - (a.is_default === true);
        });

        setParsedData({ ...parsedData, rows: sortedRows });
      }

      setIsDefaultPresent(parsedData.rows[0]?.is_default ?? false);
    }
  }, [parsedData, setIsDefaultPresent, setParsedData]);

  return (<>
    <Card>
      <CardHeader
        avatar={<IconUsers />}
        action={
          <Stack direction="row" spacing={1}>
            {!isDefaultPresent &&
              <MyButton startIcon={<IconPlus />}
                onClick={setIsDefaultProviderDialogVisible.bind(this, true)}
                sx={{ color: '#fff' }}>
                Add Default Provider
              </MyButton>
            }

            <MyButton startIcon={<IconPlus />}
              onClick={setIsProviderDialogVisible.bind(this, true)}
              sx={{ color: '#fff' }}>
              Add Provider
            </MyButton>
          </Stack>
        }
        title="Providers"
      />
      <CardContent>
        <Grid sx={{ m: -3 }}>
          <ManageProvidersTable
            onAdd={handleOnAdd}
            loading={loading || deleteClinicProviderLoading}
            data={parsedData}
            showToolbar={false}
          />
        </Grid>
      </CardContent>
    </Card>
    <AddClinicProviderDialog clinicId={clinicId}
      open={isProviderDialogVisible}
      onClose={handleCloseAddProviderDialog} />

    <AddClinicProviderDialog clinicId={clinicId}
      open={isDefaultProviderDialogVisible}
      isDefault={true}
      label={"Add Default Provider"}
      onClose={handleCloseAddProviderDialog}
      defaultValues={{
        firstname: DEFAULT_PROVIDER_NAME,
        lastname: '',
        title: '',
        can_receive: true,
        specializations: [],
        required_documents: [],
        optional_documents: [],
        is_default: true
      }} />

    <UpdateClinicProviderDialog clinicId={clinicId}
      open={updateProviderDialogVisible}
      onClose={handleUpdateProviderDialogClose}
      providerData={providerData} />
  </>);
}